import { create } from 'zustand';

export type WcMethod =
  | 'solana_signMessage'
  | 'solana_signTransaction'
  | 'solana_signAndSendTransaction';

export interface WcPendingRequest {
  id: number;
  topic: string;
  chainId: string;
  method: WcMethod;
  params: unknown;
  receivedAt: number;
  origin: string | null;
  status: 'pending' | 'in_progress' | 'done' | 'rejected' | 'failed';
}

export interface WcConnectedDapp {
  topic: string;
  name: string;
  url: string;
  iconUrl: string | null;
  connectedAt: number;
}

interface WalletConnectState {
  initialized: boolean;
  initializing: boolean;
  connectedDapps: WcConnectedDapp[];
  pending: WcPendingRequest[];
  history: WcPendingRequest[];
  error: string | null;
  activeRequest: () => WcPendingRequest | null;
  upsertDapp: (d: WcConnectedDapp) => void;
  removeDapp: (topic: string) => void;
  enqueue: (req: Omit<WcPendingRequest, 'status' | 'receivedAt'>) => void;
  setStatus: (id: number, status: WcPendingRequest['status']) => void;
  setInitialized: (v: boolean) => void;
  setInitializing: (v: boolean) => void;
  setError: (err: string | null) => void;
}

export const useWalletConnectStore = create<WalletConnectState>((set, get) => ({
  initialized: false,
  initializing: false,
  connectedDapps: [],
  pending: [],
  history: [],
  error: null,
  activeRequest: () =>
    get().pending.find((r) => r.status === 'pending' || r.status === 'in_progress') ?? null,
  upsertDapp: (d: WcConnectedDapp) => {
    const existing = get().connectedDapps.find((x) => x.topic === d.topic);
    if (existing) {
      set({
        connectedDapps: get().connectedDapps.map((x) =>
          x.topic === d.topic ? { ...x, ...d } : x,
        ),
      });
    } else {
      set({ connectedDapps: [...get().connectedDapps, d] });
    }
  },
  removeDapp: (topic: string) => {
    set({ connectedDapps: get().connectedDapps.filter((d) => d.topic !== topic) });
  },
  enqueue: (req) => {
    set({
      pending: [
        ...get().pending,
        { ...req, status: 'pending', receivedAt: Date.now() },
      ],
    });
  },
  setStatus: (id: number, status: WcPendingRequest['status']) => {
    const req = get().pending.find((r) => r.id === id);
    if (!req) return;
    const next = { ...req, status };
    if (status === 'done' || status === 'rejected' || status === 'failed') {
      set({
        history: [next, ...get().history],
        pending: get().pending.filter((r) => r.id !== id),
      });
    } else {
      set({
        pending: get().pending.map((r) => (r.id === id ? next : r)),
      });
    }
  },
  setInitialized: (v: boolean) => set({ initialized: v }),
  setInitializing: (v: boolean) => set({ initializing: v }),
  setError: (err: string | null) => set({ error: err }),
}));
